/**
 * The offline-attendance database — Dexie 4 over IndexedDB, behind ONE adapter
 * (`specs/offline-attendance-v1/implementation-plan.md`, decision D1, task FE-3).
 *
 * Everything the offline attendance stack keeps on the device lives in the
 * database `syntry-offline`: the installation id and data key (`meta`), the
 * provisioned context per scope, archived grants, the confirmed snapshot, the
 * outbox of captured punches, receipts, the device sequence, sync leases and
 * (Release 2) evidence. Rows carrying personal data are sealed
 * (`sealed.ts`); this module only declares the schema and owns the connection.
 *
 * Load-bearing decisions:
 *   - The name is `syntry-offline` and must stay that way: Workbox runtime
 *     caches are `syntry-api-*` / `syntry-media-*` (`services/cache-names.ts`)
 *     and cache cleanup must never be able to reach the attendance queue.
 *   - {@link offlineDb} is a LAZY singleton: importing this module or calling
 *     it opens nothing; Dexie opens the database on first use.
 *   - `versionchange` (a newer build in another tab upgrading the schema, or a
 *     delete) closes this connection FOR GOOD and drops the singleton: the
 *     upgrade is never blocked by us, a stale reference fails loudly
 *     (`DatabaseClosedError`) instead of silently reopening over a schema it
 *     does not know, and the next {@link offlineDb} call opens a fresh one.
 *   - Every read-write transaction is STRICTLY durable (plan amendment C14):
 *     since Chrome 121 the default is "relaxed" — `complete` fires once the data
 *     reaches the OS buffer, before any flush — and a power loss or OS crash
 *     right after the UI said "saved" could lose the punch. Engines other than
 *     Chromium ignore the option; the writes are a few small rows.
 *   - The database can disappear under a running session — another tab deletes
 *     it, the user clears site data, the browser evicts it — and come back EMPTY
 *     on the next open. {@link offlineDbGeneration} and {@link onOfflineDbWiped}
 *     report it, so nothing derived from the old database (the cached data key,
 *     the installation id, a scope) keeps being used against the new one. Every
 *     database is stamped with a random INCARNATION when IndexedDB creates it
 *     (`meta.db_incarnation`) and every open compares it with the one this
 *     context saw last — so even a database deleted and re-created by another
 *     tab while this page sat in the back/forward cache, which no event can
 *     report to a closed connection, is caught. One loss is reported once.
 *   - Schema changes are additive and versioned (`version(2)` adds, never
 *     drops): an update must never cost a queued punch.
 *
 * {@link probeStorageHealth} is the readiness check of spec §3 "Storage":
 * persistence asked and recorded, quota estimated, and a real write/read/delete
 * transaction before storage counts as ready.
 */
import Dexie, { type Table, type Transaction } from 'dexie';
import type { OutboxRow, ReceiptRow, SealedBlob } from '@/types/offline-attendance';
import { IS_DEV } from '@/utils/env';
import { newUuidV4 } from '@/utils/uuid';

/** The IndexedDB database name. Never a `syntry-api-*` / `syntry-media-*` cache name. */
export const OFFLINE_DB_NAME = 'syntry-offline';

/** The schema version {@link SyntryOfflineDb} declares. */
export const OFFLINE_DB_VERSION = 1;

/**
 * The v1 `stores()` spec. The first entry of each table is its primary key
 * (`&` — unique; `[a+b]` — compound), the rest are secondary indexes.
 * Frozen: Dexie must never be handed a shared object it could extend.
 */
export const OFFLINE_DB_SCHEMA_V1 = Object.freeze({
  meta: '&key',
  offlineContexts: '&scopeKey',
  grants: '&[scopeKey+grantId], scopeKey',
  attendanceSnapshots: '&scopeKey',
  outbox: '&clientEventId, scopeKey, [scopeKey+deviceSeq], [scopeKey+status]',
  attendanceReceipts: '&clientEventId, scopeKey',
  sequences: '&name',
  syncLeases: '&scopeKey',
  evidence: '&id, scopeKey, clientEventId',
} as const);

/**
 * Reserved `meta` keys, in one place so no two owners can collide on one.
 * Owners: installation id — the scoped repository (FE-4); data key + marker —
 * `offline-keys.ts`; clock progression — the orchestrator (FE-9); incarnation —
 * this module (written once, when IndexedDB creates the database, and never
 * changed; see {@link offlineDbGeneration}); probe — {@link probeStorageHealth}
 * (never left behind).
 */
export const OFFLINE_META_KEYS = Object.freeze({
  installationId: 'installation_id',
  dataKey: 'data_key',
  dataKeyMarker: 'data_key_marker',
  clockLastSeen: 'clock_last_seen',
  incarnation: 'db_incarnation',
  probe: '__probe__',
} as const);

/** `meta`: small device-wide values (installation id, data key, markers). */
export interface OfflineMetaRow {
  key: string;
  value: unknown;
}

/**
 * The value of `meta.data_key_marker`, written by `offline-keys.ts`: the id of
 * the data key every sealed row of THIS database is sealed under, and where
 * that key is kept. It is what tells a first run from key loss — and what the
 * repository checks a capture against before committing it (a row sealed under
 * a key the marker does not name could never be opened again).
 */
export interface DataKeyMarker {
  keyId: string;
  custody: 'webcrypto' | 'native';
}

/** A data key id: 16 base64url characters (web: 12 random bytes; native: the key's SHA-256 fingerprint prefix). */
const DATA_KEY_ID_RE = /^[A-Za-z0-9_-]{16}$/;

/** Whether `value` is a data key id as `offline-keys.ts` mints them. */
export function isDataKeyId(value: unknown): value is string {
  return typeof value === 'string' && DATA_KEY_ID_RE.test(value);
}

/**
 * A stored `meta.data_key_marker` value, read strictly — or `null` when it is
 * not one. An unreadable marker still says that a key existed: callers must
 * never read `null` as "first run" when a marker row is present.
 */
export function parseDataKeyMarker(value: unknown): DataKeyMarker | null {
  if (typeof value !== 'object' || value === null) return null;
  const keyId: unknown = Reflect.get(value, 'keyId');
  const custody: unknown = Reflect.get(value, 'custody');
  if (!isDataKeyId(keyId)) return null;
  if (custody !== 'webcrypto' && custody !== 'native') return null;
  return { keyId, custody };
}

/** `offlineContexts`: the provisioned context of one scope (sealed `OfflineContextRecord`). */
export interface OfflineContextRow {
  scopeKey: string;
  sealed: SealedBlob;
  updatedAt: number;
}

/** `grants`: every grant a scope was issued, kept while events still reference it (sealed `StoredGrant`). */
export interface GrantArchiveRow {
  scopeKey: string;
  grantId: string;
  validUntilMs: number;
  sealed: SealedBlob;
  archivedAt: number;
}

/** `attendanceSnapshots`: the newest confirmed snapshot of one scope (sealed `ConfirmedSnapshotRecord`). */
export interface AttendanceSnapshotRow {
  scopeKey: string;
  asOfMs: number;
  fetchedAtMs: number;
  sealed: SealedBlob;
}

/** `sequences`: named monotonic counters (the device-wide `device_seq`). */
export interface SequenceRow {
  name: string;
  value: number;
}

/** `syncLeases`: the single sync owner of one scope (decision D11). */
export interface SyncLeaseRow {
  scopeKey: string;
  ownerId: string;
  generation: number;
  expiresAt: number;
}

/** `evidence`: photo/GPS references — empty in Release 1 (decision D15), declared for forward compatibility. */
export interface EvidenceRow {
  id: string;
  scopeKey: string;
  clientEventId: string;
}

/** The `syntry-offline` database. Construct through {@link offlineDb}, never directly in app code. */
export class SyntryOfflineDb extends Dexie {
  meta!: Table<OfflineMetaRow, string>;
  offlineContexts!: Table<OfflineContextRow, string>;
  grants!: Table<GrantArchiveRow, [string, string]>;
  attendanceSnapshots!: Table<AttendanceSnapshotRow, string>;
  outbox!: Table<OutboxRow, string>;
  attendanceReceipts!: Table<ReceiptRow, string>;
  sequences!: Table<SequenceRow, string>;
  syncLeases!: Table<SyncLeaseRow, string>;
  evidence!: Table<EvidenceRow, string>;

  constructor() {
    // Strict durability (C14): a read-write transaction completes only once the
    // data is flushed to disk, so "saved" survives an OS crash or power loss.
    super(OFFLINE_DB_NAME, { chromeTransactionDurability: 'strict' });
    // A copy: Dexie keeps (and on a repeated stores() call extends) the object it is given.
    this.version(OFFLINE_DB_VERSION).stores({ ...OFFLINE_DB_SCHEMA_V1 });
  }
}

/* ── The connection ────────────────────────────────────────────────────── */

/** The `meta` table's name, for handles the schema does not type (the upgrade transaction, the vip database). */
const META_TABLE = 'meta';

let singleton: SyntryOfflineDb | null = null;

/**
 * The IndexedDB factory the singleton was built over. Dexie reads its backend
 * (`Dexie.dependencies.indexedDB`) once, at construction; it never changes in a
 * browser, but a test swaps in a fresh factory per test — an instance built
 * over the previous one would read the previous test's world.
 */
let singletonBackend: unknown = null;

/**
 * The incarnation stamp this context read when it last opened the database, and
 * over which backend — or `null` when there is nothing to compare with: never
 * opened yet, or a loss was just reported (then whatever opens next — a database
 * re-created empty, or the same one after a forced close — is the new baseline,
 * not a second loss). A database an older build created has no stamp: it reads
 * as `incarnation: null`, and stays that until it is re-created (and stamped).
 */
let baseline: { backend: unknown; incarnation: string | null } | null = null;

/** See {@link offlineDbGeneration}. */
let generation = 0;

/** See {@link onOfflineDbWiped}. */
const wipeListeners = new Set<() => void>();

/**
 * The offline database. Lazy: creating it opens nothing — Dexie opens on first
 * use. Always the same instance until {@link closeOfflineDb}, a
 * `versionchange`, or (tests) a swapped IndexedDB backend replaces it.
 */
export function offlineDb(): SyntryOfflineDb {
  const backend: unknown = Dexie.dependencies.indexedDB;
  if (singleton !== null && singletonBackend === backend) return singleton;
  if (singleton !== null) retire(singleton);

  const db = new SyntryOfflineDb();
  db.on('versionchange', (event: IDBVersionChangeEvent) => {
    if (IS_DEV) {
      console.warn(`[offline-db] another connection is upgrading or deleting '${OFFLINE_DB_NAME}' — closing this one`);
    }
    retire(db);
    // No new version: the database is being DELETED, and everything derived
    // from it (the data key, the installation id) goes with it.
    if (event.newVersion === null) databaseWiped();
    // Handled: skip Dexie's default handler, which would re-enable auto-open on
    // this now-stale instance.
    return false;
  });
  db.on('blocked', (event: IDBVersionChangeEvent) => {
    if (IS_DEV) {
      console.warn(`[offline-db] opening '${OFFLINE_DB_NAME}' is blocked by another connection`, event.oldVersion, event.newVersion);
    }
  });
  // Every end of a connection — this app closing it, an upgrade elsewhere, the
  // back/forward cache, the browser — ends what was cached against it.
  db.on('close', () => {
    generation += 1;
  });
  // IndexedDB is creating the database from scratch: stamp it inside the upgrade
  // transaction, so the stamp exists exactly as long as the database does. A
  // database created again later — by this context or any other — gets another.
  db.on('populate', (tx: Transaction) =>
    tx.table(META_TABLE).add({ key: OFFLINE_META_KEYS.incarnation, value: newIncarnation() }),
  );
  // After every successful open (sticky: auto-reopens too). Dexie awaits it
  // before the open resolves; it never rejects (see onOpened).
  db.on('ready', (vipDb: Dexie) => onOpened(db, vipDb, backend), true);
  singleton = db;
  singletonBackend = backend;
  return db;
}

/**
 * A connection just opened: watch it for a forced close, then compare the
 * database's incarnation stamp with the one this context saw last. A different
 * stamp means the database was deleted and created again while this context was
 * not connected to it — re-created by this very open, or by another tab while
 * this page sat in the back/forward cache (no versionchange can reach a closed
 * connection): a wipe. Never rejects: Dexie awaits it before the open resolves,
 * and an open must not fail over a watcher — a stamp that cannot be read is
 * simply compared on the next open.
 */
async function onOpened(db: SyntryOfflineDb, vipDb: Dexie, backend: unknown): Promise<void> {
  watchForcedClose(db);
  let incarnation: string | null;
  try {
    // The vip handle: the open is still in progress, and a plain read would wait for it.
    incarnation = readIncarnation(await vipDb.table(META_TABLE).get(OFFLINE_META_KEYS.incarnation));
  } catch (err) {
    // Unreadable this time (a transient IndexedDB failure): keep the old
    // baseline and compare on the next open.
    if (IS_DEV) console.warn('[offline-db] could not read the database incarnation', errorName(err));
    return;
  }
  if (baseline !== null && baseline.backend === backend && baseline.incarnation !== incarnation) {
    databaseWiped();
  }
  // After the report: databaseWiped() clears the baseline, and this database is the new one.
  baseline = { backend, incarnation };
}

/** A stored stamp, or `null` for none (a database an older build created) or an unusable value. */
function readIncarnation(row: unknown): string | null {
  if (typeof row !== 'object' || row === null) return null;
  const value: unknown = Reflect.get(row, 'value');
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** A fresh incarnation stamp. It only has to differ from every other creation's; it protects nothing. */
function newIncarnation(): string {
  try {
    return newUuidV4();
  } catch {
    // No Web Crypto at all: any value unlikely to repeat will do — creating the
    // database must never fail over its stamp.
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }
}

/**
 * Notice the browser closing the connection under us — IndexedDB's FORCED
 * close, the `close` event, which a normal `close()` never fires. Clearing site
 * data does exactly that (then deletes the database); so do storage eviction
 * and a lost disk. Dexie reopens on the next use, possibly over an empty
 * database, so this counts as a wipe. Never throws: an open must not fail over
 * a watcher.
 */
function watchForcedClose(db: SyntryOfflineDb): void {
  try {
    db.backendDB().addEventListener('close', databaseWiped, { once: true });
  } catch (err) {
    // No live connection to watch (it closed again before 'ready' ran): a
    // re-created database is still caught by its incarnation stamp on reopen.
    if (IS_DEV) console.warn('[offline-db] could not watch the connection for a forced close', errorName(err));
  }
}

/**
 * The database this context was using is gone — deleted, re-created from
 * scratch, or force-closed by the browser. Moves the generation now; tells the
 * subscribers on a later macrotask, so none of them runs inside an IndexedDB
 * event or an open that is still in progress.
 */
function databaseWiped(): void {
  generation += 1;
  // Reported now, so the database that opens next — re-created empty, or the
  // same one after a forced close — is the new baseline, never a second report.
  baseline = null;
  if (wipeListeners.size === 0) return;
  const listeners = [...wipeListeners];
  setTimeout(() => {
    for (const listener of listeners) {
      if (!wipeListeners.has(listener)) continue; // unsubscribed since
      try {
        listener();
      } catch (err) {
        // One subscriber's bug must never reach IndexedDB or keep the others from hearing it.
        if (IS_DEV) console.warn('[offline-db] a wipe subscriber failed', errorName(err));
      }
    }
  }, 0);
}

/**
 * The connection generation: a number that moves whenever this context's
 * connection to the offline database ENDS — closed by this app, by Dexie for
 * the back/forward cache, by another connection's upgrade or delete, by the
 * browser — or the database is re-created from scratch. Anything derived from
 * the database and cached in memory (the data key, `offline-keys.ts`) is only
 * good while this number stays the same: a database deleted while nothing was
 * connected gives no other signal until it is reopened EMPTY. Not persisted;
 * compare values within one JS context only.
 */
export function offlineDbGeneration(): number {
  return generation;
}

/**
 * Be told when the database this context was using may have LOST its data:
 * another connection deleted it; it came back re-created from scratch — by this
 * context, or by another tab while this one was not connected (the
 * back/forward cache), caught by its incarnation stamp on the next open; or the
 * browser force-closed it (clearing site data). Not for an ordinary close, a
 * reopen or a schema upgrade, and ONE loss is reported ONCE (a deletion is not
 * reported again when the database is then re-created). Rows written from then
 * on must not be sealed under a key, or keyed by an installation id, resolved
 * from the old database: a subscriber (the orchestrator) stops capture and
 * resolves the key, the installation id and the scope again before going on.
 *
 * Subscribers run on a later macrotask, each isolated (a throwing one is
 * logged in dev and skipped). Returns the unsubscribe function; a subscriber
 * that unsubscribes before the notification runs is not called.
 */
export function onOfflineDbWiped(listener: () => void): () => void {
  wipeListeners.add(listener);
  return () => {
    wipeListeners.delete(listener);
  };
}

/** Close `db` for good (no auto-reopen) and forget it if it is the singleton. */
function retire(db: SyntryOfflineDb): void {
  if (singleton === db) {
    singleton = null;
    singletonBackend = null;
  }
  db.close();
}

/**
 * Close the singleton's connection and forget it; the next {@link offlineDb}
 * call opens a fresh one. Safe to call when nothing is open. Specs call it in
 * `afterEach` (with `resetDataKeyCacheForTests()`) so no test reuses another's
 * connection. It ends the connection, so it moves {@link offlineDbGeneration}.
 */
export async function closeOfflineDb(): Promise<void> {
  const db = singleton;
  singleton = null;
  singletonBackend = null;
  db?.close();
}

/** The name {@link probeStorageHealth} reports when there is no IndexedDB at all. */
const MISSING_API = 'MissingAPIError';

/**
 * Why IndexedDB cannot be used here, or `null` when it can: `MissingAPIError`
 * when the global or the copy Dexie reads (`Dexie.dependencies`, which every
 * Dexie instance actually uses) is absent — or the error name when merely
 * READING the global throws, as it does in storage-restricted contexts
 * (`SecurityError`). Never throws.
 */
function indexedDbProblem(): string | null {
  try {
    if (typeof indexedDB === 'undefined' || indexedDB === null) return MISSING_API;
  } catch (err) {
    // The global's getter itself refused (Firefox with cookies blocked, a
    // sandboxed frame): IndexedDB is not usable here — say why.
    return errorName(err);
  }
  return Dexie.dependencies.indexedDB ? null : MISSING_API;
}

/**
 * Whether this environment has IndexedDB at all — the global AND the copy Dexie
 * reads (`Dexie.dependencies`). Never throws: a context where reading the
 * global throws simply has none.
 */
export function isIndexedDbAvailable(): boolean {
  return indexedDbProblem() === null;
}

/* ── Storage health ────────────────────────────────────────────────────── */

export type StorageHealth = {
  status: 'ready' | 'failed';
  persisted: boolean | null;
  quota: number | null;
  usage: number | null;
  error: string | null;
};

/**
 * How long a StorageManager call may take. Some browsers answer `persist()`
 * only after a permission prompt the user may never answer; the probe must
 * still finish.
 */
const STORAGE_MANAGER_TIMEOUT_MS = 5_000;

/**
 * Is this device able to keep captures durably right now?
 *
 *   1. IndexedDB present, else `failed` (`MissingAPIError` — or `SecurityError`
 *      when reading the global throws).
 *   2. The offline database opens.
 *   3. Persistence: `persisted()`; if not yet persisted, `persist()` — the
 *      answer is recorded (`false` when refused or unanswered within 5 s).
 *   4. `estimate()` — quota and usage, best-effort.
 *   5. A write/read/delete of `meta.__probe__` in ONE readwrite transaction,
 *      then a read in a new transaction that must see the delete committed.
 *
 * Steps 3–4 are best-effort: a missing, throwing or silent StorageManager
 * leaves `persisted`/`quota`/`usage` unknown (`null`) — or `persisted: false`
 * when the request went unanswered — and never fails the probe (a declined
 * persistence is a DEGRADED state, spec §3). Any failure of steps 1, 2 or 5 is
 * `failed` with the error's name (`QuotaExceededError`, `AbortError`, …) and
 * keeps whatever steps 3–4 already learned.
 */
export async function probeStorageHealth(): Promise<StorageHealth> {
  const health: StorageHealth = { status: 'ready', persisted: null, quota: null, usage: null, error: null };
  const problem = indexedDbProblem();
  if (problem !== null) {
    return { ...health, status: 'failed', error: problem };
  }
  try {
    const db = offlineDb();
    await db.open();
    const manager = storageManager();
    if (manager) {
      health.persisted = await readPersistence(manager);
      const estimate = await readEstimate(manager);
      health.quota = estimate.quota;
      health.usage = estimate.usage;
    }
    await roundTrip(db);
    return health;
  } catch (err) {
    // The database refused to open, a write hit the quota, the transaction
    // aborted, or the probe did not read back: captures could not be stored
    // durably here. Report it; never throw from a health check.
    return { ...health, status: 'failed', error: errorName(err) };
  }
}

/** `navigator.storage`, which the DOM typings promise but older/embedded browsers (and happy-dom) lack. */
function storageManager(): StorageManager | undefined {
  if (typeof navigator === 'undefined') return undefined;
  const manager = navigator.storage as StorageManager | undefined;
  return manager ?? undefined;
}

async function readPersistence(manager: StorageManager): Promise<boolean | null> {
  if (typeof manager.persisted !== 'function') return null;
  const current = await settleWithin(() => manager.persisted());
  if (!current.ok || typeof current.value !== 'boolean') return null;
  if (current.value) return true;
  if (typeof manager.persist !== 'function') return false;
  const asked = await settleWithin(() => manager.persist());
  return asked.ok && asked.value === true;
}

async function readEstimate(manager: StorageManager): Promise<{ quota: number | null; usage: number | null }> {
  if (typeof manager.estimate !== 'function') return { quota: null, usage: null };
  const estimate = await settleWithin(() => manager.estimate());
  if (!estimate.ok) return { quota: null, usage: null };
  return { quota: finiteOrNull(estimate.value?.quota), usage: finiteOrNull(estimate.value?.usage) };
}

type Settled<T> = { ok: true; value: T } | { ok: false };

/**
 * `start()`'s value — or `{ ok: false }` when it throws, rejects, or has not
 * settled within {@link STORAGE_MANAGER_TIMEOUT_MS}.
 */
async function settleWithin<T>(start: () => Promise<T>): Promise<Settled<T>> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const timeout = new Promise<Settled<T>>((resolve) => {
      timer = setTimeout(() => resolve({ ok: false }), STORAGE_MANAGER_TIMEOUT_MS);
    });
    const answer = start().then(
      (value): Settled<T> => ({ ok: true, value }),
      // A rejection is "no answer": the StorageManager calls are best-effort.
      (): Settled<T> => ({ ok: false }),
    );
    return await Promise.race([answer, timeout]);
  } catch {
    // start() threw synchronously (a broken StorageManager) — also "no answer".
    return { ok: false };
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/**
 * Write, read back and delete a random probe value in one readwrite
 * transaction, then confirm from a fresh transaction that the delete committed.
 */
async function roundTrip(db: SyntryOfflineDb): Promise<void> {
  const token = `${Date.now().toString(36)}.${Math.random().toString(36).slice(2)}`;
  await db.transaction('rw', db.meta, async () => {
    await db.meta.put({ key: OFFLINE_META_KEYS.probe, value: token });
    const written = await db.meta.get(OFFLINE_META_KEYS.probe);
    if (written?.value !== token) throw probeMismatch('the probe write did not read back');
    await db.meta.delete(OFFLINE_META_KEYS.probe);
  });
  const after = await db.transaction('r', db.meta, () => db.meta.get(OFFLINE_META_KEYS.probe));
  if (after !== undefined) throw probeMismatch('the probe delete did not commit');
}

function probeMismatch(message: string): Error {
  const error = new Error(`[offline-db] ${message}`);
  error.name = 'ProbeMismatchError';
  return error;
}

function finiteOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
}

function errorName(err: unknown): string {
  if (typeof err === 'object' && err !== null) {
    const name: unknown = Reflect.get(err, 'name');
    if (typeof name === 'string' && name.length > 0) return name;
  }
  return 'UnknownError';
}
